import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { saveSFQInfo, fetchSFQDetail } from "../Redux/feasibility";
import { useCRACandidates, pmLabel } from "../usePMCandidates";

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #6c757d;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px 2px 10px;
  border-radius: 12px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
`;

const ChipX = styled.button`
  border: none;
  background: transparent;
  color: #6366f1;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 2px 4px;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { color: #dc3545; }
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #0d6efd;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const Select = styled.select`
  padding: 3px 6px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 12px;
  background: #fff;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const Muted = styled.span`
  color: #adb5bd;
  font-size: 12px;
`;

// The study's assigned CRAs, shown as chips. The owner (or Admin) may add/remove them;
// each change sends the full roster (craEmployeeNos) so the API diffs and notifies.
// Whether the add-picker is open is decided by StudyView, which keeps it and the header's
// editors mutually exclusive.
export default function CraRosterEditor({ sfq, canEdit, adding, onAdd, onDone }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const { candidates, loading, error } = useCRACandidates();

  const cras   = sfq.cras || [];
  const craSet = cras.map((c) => String(c.employeeNo));
  const addable = candidates.filter((e) => !craSet.includes(String(e.employeeNo)));

  async function commit(nextNos, actionLabel) {
    setSaving(true);
    try {
      const res = await dispatch(
        saveSFQInfo({ id: sfq.id, body: { craEmployeeNos: nextNos } })
      ).unwrap();
      await dispatch(fetchSFQDetail(sfq.id));
      if (res?.emailWarning) alert(res.emailWarning);
    } catch (e) {
      alert(e?.message || `${actionLabel}에 실패했습니다.`);
    } finally {
      setSaving(false);
      onDone();
    }
  }

  function handleAdd(e) {
    const no = e.target.value;
    if (!no) return;
    commit([...craSet, no], "CRA 추가");
  }

  function handleRemove(no) {
    if (!window.confirm("이 CRA 배정을 해제하시겠습니까?")) return;
    commit(craSet.filter((n) => n !== String(no)), "CRA 제거");
  }

  return (
    <Row>
      <span>CRA:</span>
      {cras.length === 0 && <Muted>배정된 CRA 없음</Muted>}
      {cras.map((c) => (
        <Chip key={c.employeeNo}>
          {c.name || c.employeeNo}
          {canEdit && (
            <ChipX
              title="배정 해제"
              disabled={saving}
              onClick={() => handleRemove(c.employeeNo)}
            >
              ×
            </ChipX>
          )}
        </Chip>
      ))}

      {canEdit && (
        adding ? (
          error ? (
            <Muted style={{ color: "#dc3545" }}>{error}</Muted>
          ) : (
            <Select
              autoFocus
              disabled={loading || saving}
              defaultValue=""
              onChange={handleAdd}
              onBlur={() => onDone()}
            >
              <option value="">
                {loading ? "불러오는 중..." : addable.length ? "추가할 CRA 선택" : "추가할 CRA 없음"}
              </option>
              {addable.map((e) => (
                <option key={e.employeeNo} value={e.employeeNo}>
                  {pmLabel(e)}{e.jd ? ` — ${e.jd}` : ""}
                </option>
              ))}
            </Select>
          )
        ) : (
          <LinkBtn disabled={saving} onClick={onAdd}>+ CRA 추가</LinkBtn>
        )
      )}
      {saving && <Muted>저장 중...</Muted>}
    </Row>
  );
}
